import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enviar cookies httpOnly com TODAS as requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tentar restaurar sessão a partir dos cookies JWT
  const checkSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me`);
      setUser(res.data);
    } catch {
      // Sem sessão válida — tentar refresh
      try {
        await axios.post(`${API}/refresh`);
        const res = await axios.get(`${API}/me`);
        setUser(res.data);
      } catch {
        // Sem cookies válidos — verificar localStorage (fallback)
        const stored = localStorage.getItem('fabriflow_user');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            // Re-login silencioso com token guardado
            if (parsed.token) {
              const res = await axios.get(`${API}/me`, {
                headers: { Authorization: `Bearer ${parsed.token}` }
              });
              setUser(res.data);
            }
          } catch {
            localStorage.removeItem('fabriflow_user');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Interceptor para refresh automático em 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/login') &&
          !originalRequest.url?.includes('/me') &&
          !originalRequest.url?.includes('/refresh')
        ) {
          originalRequest._retry = true;
          try {
            await axios.post(`${API}/refresh`);
            return axios(originalRequest);
          } catch {
            setUser(null);
            localStorage.removeItem('fabriflow_user');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (username, password = null) => {
    const payload = { username };
    if (password) payload.password = password;
    const response = await axios.post(`${API}/login`, payload);
    const userData = response.data;
    setUser(userData);
    localStorage.setItem('fabriflow_user', JSON.stringify(userData));
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/logout`);
    } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem('fabriflow_user');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
